import React, { Suspense } from 'react'

import {
  ConfirmationDialog,
  ModalInjectedProps,
  Skeleton,
} from '@sketch/components'
import { useStorageItemsCount } from '../../hooks/useStorageItemsCount'
import { StorageItemIdentity } from '../../types'
import { useDeleteStorageItemsMutation } from '@sketch/gql-types'
import { useToast } from '@sketch/toasts'
import { Link } from 'react-router-dom'
import { deleteWorkspaceProject, routes } from '@sketch/modules-common'
import { handleShareDeleted } from '../../../shares/operations/handlers'
import { useEventDispatch } from '@sketch/utils'
import { useGetStorageItem } from '../../hooks'

interface DeleteStorageItemMessageProps {
  item: StorageItemIdentity
}

const DeleteStorageItemMessage = ({ item }: DeleteStorageItemMessageProps) => {
  const storageItem = useGetStorageItem(item)
  return `“${storageItem?.name}”`
}

interface DeleteStorageItemsMessageProps {
  items: StorageItemIdentity[]
}

const DeleteStorageItemsMessage = ({
  items,
}: DeleteStorageItemsMessageProps) => {
  const { message } = useStorageItemsCount(items)
  return message
}

interface DeleteStorageItemsModalProps extends ModalInjectedProps {
  items: StorageItemIdentity[]
  workspaceId: string
  onDelete?: () => void
}

export const DeleteStorageItemsModal: React.FC<
  DeleteStorageItemsModalProps
> = props => {
  const { items, hideModal, isModalOpen, workspaceId, onDelete } = props

  const { showToast } = useToast()

  const dispatchTrashRefresh = useEventDispatch('workspaceTrashRefresh')
  const dispatchShareRefresh = useEventDispatch('workspaceShareRefresh')

  const [deleteItems, { loading }] = useDeleteStorageItemsMutation({
    variables: {
      storageItems: items.map(item => ({
        identifier: item.identifier,
        type: item.__typename === 'Share' ? 'SHARE' : 'PROJECT',
      })),
    },
    onCompleted: () => {
      dispatchTrashRefresh({ workspaceIdentifier: workspaceId })
      dispatchShareRefresh({ workspaceIdentifier: workspaceId })

      hideModal()
      onDelete?.()

      showToast(
        <>
          Selection has been moved to{' '}
          <Link
            to={routes.WORKSPACE_TRASH.create({
              workspaceId,
            })}
          >
            Trash
          </Link>
        </>,
        'positive'
      )
    },
    update: (cache, { data }) => {
      const items = data?.deleteStorageItems.storageItems || []

      items.forEach(item => {
        if (item.__typename === 'Share') {
          handleShareDeleted({
            cache,
            identifier: item.identifier,
            workspaceIdentifier: workspaceId,
          })
        } else {
          deleteWorkspaceProject(cache, workspaceId, item.identifier)
        }

        cache.evict({ id: cache.identify(item) })
      })
    },
    onError: 'show-toast',
  })

  const message =
    items.length === 1 ? (
      <DeleteStorageItemMessage item={items[0]} />
    ) : (
      <DeleteStorageItemsMessage items={items} />
    )

  const title =
    items.length === 1 ? 'Move to Trash?' : 'Move Selection to Trash?'

  return (
    <ConfirmationDialog
      title={title}
      hideModal={hideModal}
      isModalOpen={isModalOpen}
      cancelButton={{
        disabled: loading,
      }}
      confirmButton={{
        text: 'Move to Trash',
        variant: 'negative',
        loading,
        disabled: loading,
      }}
      onConfirm={() => {
        deleteItems()
      }}
    >
      <>
        <p>
          Move{' '}
          <Suspense fallback={<Skeleton width="100px" />}>
            <strong>{message}</strong>
          </Suspense>{' '}
          to Trash?
        </p>
        <p>
          Documents in Trash will be permanently deleted after 90 days.
          Workspace Members with access can still see it in Trash.
        </p>
      </>
    </ConfirmationDialog>
  )
}
